import { useQuery } from '@tanstack/react-query'
import { Gauge as GaugeIcon } from 'lucide-react'
import { convictionApi } from '../api/client'
import { THETA_MAX, THETA_MIN } from '../lib/theta'
import { ConfidenceHeatmap } from './ConfidenceHeatmap'
import './ValvePanel.css'

interface ValvePanelProps {
  accountId: number
}

/**
 * Thesis-break valve summary: a theta dial (identical in spirit to
 * ConvictionStrip's) plus exit/log stat readouts, and the confidence
 * heatmap hero visualization underneath.
 */
export function ValvePanel({ accountId }: ValvePanelProps) {
  const statusQuery = useQuery({
    queryKey: ['conviction-status', accountId],
    queryFn: () => convictionApi.status(accountId),
    retry: 1,
    refetchInterval: 30000,
  })
  const scorecardQuery = useQuery({
    queryKey: ['scorecard', accountId],
    queryFn: () => convictionApi.scorecard(accountId),
    retry: 1,
    refetchInterval: 30000,
  })

  const theta = statusQuery.data?.theta ?? 0
  const thetaPct = Math.min(100, Math.max(0, ((theta - THETA_MIN) / (THETA_MAX - THETA_MIN)) * 100))
  const valve = scorecardQuery.data?.valve
  const isLoading = statusQuery.isLoading || scorecardQuery.isLoading
  const isError = statusQuery.isError || scorecardQuery.isError
  const hasData = statusQuery.data !== undefined

  return (
    <div className="card valve-panel">
      <div className="card-header">
        <h3 className="card-title">
          <GaugeIcon size={18} className="icon" aria-hidden="true" />
          Thesis-break valve
        </h3>
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError || !hasData ? (
        <div className="empty-state">
          <div className="empty-icon"><GaugeIcon size={28} aria-hidden="true" /></div>
          <div className="empty-text">valve data unavailable</div>
        </div>
      ) : (
        <>
          <div className="valve-panel-dial-block">
            <div className="valve-panel-dial-header">
              <span className="valve-panel-dial-label">θ thesis valve</span>
              <span className="valve-panel-dial-value num">{theta.toFixed(2)}</span>
            </div>
            <div className="valve-panel-dial-track">
              <div className="valve-panel-dial-marker" style={{ left: `${thetaPct}%` }} />
            </div>
            <div className="valve-panel-dial-bounds">
              <span>{THETA_MIN.toFixed(2)}</span>
              <span>{THETA_MAX.toFixed(2)}</span>
            </div>
          </div>

          <div className="valve-panel-stats">
            <div className="valve-panel-stat">
              <span className="caption">Thesis-break exits</span>
              <span className="num valve-panel-stat-value">{valve?.thesis_break_exits ?? 0}</span>
            </div>
            <div className="valve-panel-stat">
              <span className="caption">Confidence log rows</span>
              <span className="num valve-panel-stat-value">{valve?.confidence_log_rows ?? 0}</span>
            </div>
          </div>

          <div className="valve-panel-heatmap">
            <ConfidenceHeatmap accountId={accountId} theta={theta} />
          </div>
        </>
      )}
    </div>
  )
}
